import React, { useState } from 'react';
import '../styles/Parser.css';

const JDParser = ({ onParsed, onClose }) => {
  const [mode, setMode] = useState('text'); // 'text' or 'file'
  const [jdText, setJdText] = useState('');
  const [file, setFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(selectedFile.type)) {
        setError('Please upload a PDF, DOCX, or TXT file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleParse = async () => {
    setError('');
    setIsParsing(true);

    try {
      const token = localStorage.getItem('admin_token');
      let response;

      if (mode === 'text') {
        if (!jdText.trim()) {
          setError('Please enter job description text');
          setIsParsing(false);
          return;
        }

        response = await fetch(`${process.env.REACT_APP_ADMIN_API_BASE}/requirements/parse-jd`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ text: jdText })
        });
      } else {
        if (!file) {
          setError('Please select a file to upload');
          setIsParsing(false);
          return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('store_original', 'true');

        response = await fetch(`${process.env.REACT_APP_ADMIN_API_BASE}/requirements/parse-jd-file`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      }

      const result = await response.json();

      if (result.success) {
        setParsedData(result);
        setShowPreview(true);
      } else {
        setError(result.error || 'Failed to parse job description');
      }
    } catch (err) {
      console.error('Parse error:', err);
      setError('Failed to parse job description. Please try again.');
    } finally {
      setIsParsing(false);
    }
  };

  const handleApply = () => {
    if (parsedData && parsedData.parsed_data) {
      onParsed(parsedData.parsed_data, parsedData.fields_filled);
      onClose();
    }
  };

  const renderPreview = () => {
    if (!showPreview || !parsedData) return null;

    const { parsed_data, fields_filled } = parsedData;

    return (
      <div className="parse-preview-modal">
        <div className="parse-preview-content">
          <h3>Parsed Data Preview</h3>
          <p className="preview-info">
            <span className="auto-fill-badge">{fields_filled.length} fields</span> auto-filled from your job description
          </p>

          <div className="preview-sections">
            {parsed_data.jobTitle && (
              <div className="preview-field">
                <label>Job Title:</label>
                <span className="auto-filled-value">{parsed_data.jobTitle}</span>
              </div>
            )}

            {parsed_data.department && (
              <div className="preview-field">
                <label>Department:</label>
                <span className="auto-filled-value">{parsed_data.department}</span>
              </div>
            )}

            {parsed_data.level && (
              <div className="preview-field">
                <label>Level:</label>
                <span className="auto-filled-value">{parsed_data.level}</span>
              </div>
            )}

            {parsed_data.jobLocation && (
              <div className="preview-field">
                <label>Location:</label>
                <span className="auto-filled-value">{parsed_data.jobLocation}</span>
              </div>
            )}

            {parsed_data.employmentType && (
              <div className="preview-field">
                <label>Employment Type:</label>
                <span className="auto-filled-value">{parsed_data.employmentType}</span>
              </div>
            )}

            {parsed_data.experienceLevel && (
              <div className="preview-field">
                <label>Experience Level:</label>
                <span className="auto-filled-value">{parsed_data.experienceLevel}</span>
              </div>
            )}

            {parsed_data.skillsAndCompetencies?.technicalSkills?.length > 0 && (
              <div className="preview-field">
                <label>Technical Skills:</label>
                <div className="skills-preview">
                  {parsed_data.skillsAndCompetencies.technicalSkills.slice(0, 10).map((skill, idx) => (
                    <span key={idx} className="skill-tag">{skill}</span>
                  ))}
                  {parsed_data.skillsAndCompetencies.technicalSkills.length > 10 && (
                    <span className="more-indicator">+{parsed_data.skillsAndCompetencies.technicalSkills.length - 10} more</span>
                  )}
                </div>
              </div>
            )}

            {parsed_data.jobResponsibilities?.primaryResponsibilities?.length > 0 && (
              <div className="preview-field">
                <label>Responsibilities:</label>
                <ul className="responsibilities-preview">
                  {parsed_data.jobResponsibilities.primaryResponsibilities.slice(0, 5).map((resp, idx) => (
                    <li key={idx}>{resp}</li>
                  ))}
                  {parsed_data.jobResponsibilities.primaryResponsibilities.length > 5 && (
                    <li className="more-indicator">+{parsed_data.jobResponsibilities.primaryResponsibilities.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          <div className="preview-actions">
            <button className="btn-secondary" onClick={() => setShowPreview(false)}>
              Back to Edit
            </button>
            <button className="btn-primary" onClick={handleApply}>
              Apply to Form
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="jd-parser">
      <div className="parser-header">
        <h3>Parse Job Description</h3>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="parser-tabs">
        <button
          className={`tab ${mode === 'text' ? 'active' : ''}`}
          onClick={() => setMode('text')}
        >
          Paste Text
        </button>
        <button
          className={`tab ${mode === 'file' ? 'active' : ''}`}
          onClick={() => setMode('file')}
        >
          Upload File
        </button>
      </div>

      <div className="parser-content">
        {mode === 'text' ? (
          <div className="text-input-section">
            <label>Paste your job description below:</label>
            <textarea
              value={jdText}
              onChange={(e) => setJdText(e.target.value)}
              placeholder="Paste the complete job description here including job title, requirements, responsibilities, skills, etc."
              rows={20}
              className="jd-textarea"
            />
          </div>
        ) : (
          <div className="file-input-section">
            <label>Upload job description file (PDF, DOCX, or TXT):</label>
            <div className="file-upload-area">
              <input
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                onChange={handleFileChange}
                id="jd-file-input"
              />
              <label htmlFor="jd-file-input" className="file-upload-label">
                {file ? (
                  <div className="file-selected">
                    <span className="file-icon">📄</span>
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">({(file.size / 1024).toFixed(2)} KB)</span>
                  </div>
                ) : (
                  <div className="file-placeholder">
                    <span className="upload-icon">📁</span>
                    <p>Click to select file or drag and drop</p>
                    <p className="file-hint">Supported: PDF, DOCX, TXT</p>
                  </div>
                )}
              </label>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="parser-actions">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleParse}
            disabled={isParsing || (mode === 'text' && !jdText.trim()) || (mode === 'file' && !file)}
          >
            {isParsing ? (
              <>
                <span className="spinner"></span>
                Parsing...
              </>
            ) : (
              'Parse Job Description'
            )}
          </button>
        </div>
      </div>

      {renderPreview()}
    </div>
  );
};

export default JDParser;
